import React from "react";

export default function ResponsiveFields({
	items = [],
	columns = [],
	columnsClassName = "",
	getKey,
	showHeader = true,
	variant = "rows",
	className = "",
}) {
	if (!Array.isArray(items) || items.length === 0) return null;
	if (!Array.isArray(columns) || columns.length === 0) return null;

	const isCards = variant === "cards";
	const isTable = variant === "table";

	const effectiveShowHeader = isCards ? false : showHeader;
	const effectiveColumnsClassName = columnsClassName || "sm:auto-cols-max";

	const gridTemplateColumns = columns.map((c) => c.width || "minmax(0, max-content)").join(" ");
	const templateStyle = { gridTemplateColumns };

	const renderHeader = () => {
		if (!effectiveShowHeader) return null;

		return (
			<div
				className={`hidden sm:grid ${effectiveColumnsClassName} gap-2 px-2 pb-2 font-bold`}
				style={templateStyle}
			>
				{columns.map((c) => (
					<div key={c.key} className="min-w-0">
						{c.label}
					</div>
				))}
			</div>
		);
	};

	const renderCell = (c, item, idx) => (
		<div key={c.key} className="min-w-0">
			<div
				className={`text-xs font-semibold text-secondary ${
					effectiveShowHeader ? "sm:hidden" : ""
				}`}
			>
				{c.label}
			</div>
			<div className={c.valueClassName || ""}>
				{typeof c.render === "function" ? c.render(item, idx) : null}
			</div>
		</div>
	);

	const renderRow = (item, idx) => {
		const key = getKey ? getKey(item, idx) : idx;

		return (
			<div
				key={key}
				className={
					isCards
						? `p-3 border rounded-lg bg-light grid gap-2 ${effectiveColumnsClassName}`
						: `p-3 border rounded-lg bg-light sm:bg-transparent sm:border-0 sm:rounded-none sm:grid ${effectiveColumnsClassName} sm:gap-2 sm:p-2`
				}
				style={isCards ? undefined : templateStyle}
			>
				{columns.map((c) => renderCell(c, item, idx))}
			</div>
		);
	};

	const renderCards = () => (
		<>
			{renderHeader()}
			<div
				className={
					isCards
						? "space-y-3"
						: "space-y-3 sm:space-y-0 sm:divide-y sm:divide-surface/30"
				}
			>
				{items.map(renderRow)}
			</div>
		</>
	);

	const renderTable = () => (
		<div className="hidden sm:block overflow-x-auto">
			<table className="min-w-full border-collapse">
				{showHeader && (
					<thead>
						<tr>
							{columns.map((c) => (
								<th
									key={c.key}
									className="px-2 py-2 text-left text-xs font-bold border-b"
									style={c.width ? { width: c.width } : undefined}
								>
									{c.label}
								</th>
							))}
						</tr>
					</thead>
				)}
				<tbody>
					{items.map((item, idx) => {
						const key = getKey ? getKey(item, idx) : idx;
						return (
							<tr key={key} className="border-b last:border-none">
								{columns.map((c) => (
									<td
										key={c.key}
										className={`px-2 py-3 align-top ${
											c.valueClassName || ""
										}`}
										style={c.width ? { width: c.width } : undefined}
									>
										{typeof c.render === "function"
											? c.render(item, idx)
											: null}
									</td>
								))}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);

	if (isTable) {
		return (
			<div className={className}>
				<div className="block sm:hidden">{renderCards()}</div>
				{renderTable()}
			</div>
		);
	}

	return <div className={className}>{renderCards()}</div>;
}